/**
 * AccountSelectorModal - Modal for selecting Meta ad account
 * Follows ConfirmDeleteModal pattern (createPortal + ModalOverlay)
 */

import { CheckCircle, X, Loader2 } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { MetaAdAccount } from '@/types/meta';

// ============================================================================
// TYPES
// ============================================================================

export interface AccountSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (accountId: string) => void;
  accounts: MetaAdAccount[];
  isLoading?: boolean;
  isConnecting?: boolean;
}

// ============================================================================
// MODAL OVERLAY COMPONENT
// ============================================================================

function ModalOverlay({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClick();
        }
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AccountSelectorModal({
  isOpen,
  onClose,
  onSelect,
  accounts,
  isLoading = false,
  isConnecting = false,
}: AccountSelectorModalProps) {
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Reset selection when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedAccountId(null);
    }
  }, [isOpen]);

  // Handle escape key (blocked during connecting)
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isConnecting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isConnecting, onClose]);

  // Focus trap
  React.useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  const handleConnect = () => {
    if (selectedAccountId) {
      onSelect(selectedAccountId);
    }
  };

  if (!isOpen) return null;

  // Sort accounts alphabetically by name
  const sortedAccounts = [...accounts].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const modalContent = (
    <ModalOverlay onClick={isConnecting ? () => {} : onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        tabIndex={-1}
        className={cn(
          'w-full max-w-md bg-surface rounded-xl shadow-xl border border-border-subtle',
          'animate-in zoom-in-95 slide-in-from-bottom-2 duration-200',
          'focus:outline-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h2
              id="modal-title"
              className="text-lg font-semibold text-primary leading-tight"
            >
              Select Ad Account
            </h2>
            <p
              id="modal-description"
              className="mt-1 text-sm text-secondary"
            >
              Choose which Meta ad account to connect
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isConnecting}
            className={cn(
              'flex-shrink-0 p-1 rounded-md text-muted hover:text-primary hover:bg-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
              'disabled:opacity-50 disabled:pointer-events-none'
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Account List */}
        <div className="px-6 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-brand animate-spin" />
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {sortedAccounts.map((account) => (
                <label
                  key={account.id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    selectedAccountId === account.id
                      ? 'border-brand bg-brand/5'
                      : 'border-border-subtle hover:bg-surface-subtle',
                    isConnecting && 'pointer-events-none opacity-50'
                  )}
                >
                  <input
                    type="radio"
                    name="account"
                    value={account.id}
                    checked={selectedAccountId === account.id}
                    onChange={() => setSelectedAccountId(account.id)}
                    disabled={isConnecting}
                    className="sr-only"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-primary truncate">{account.name}</p>
                    <p className="text-sm text-secondary">{account.id}</p>
                  </div>
                  {selectedAccountId === account.id && (
                    <CheckCircle className="h-5 w-5 text-brand flex-shrink-0" />
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isConnecting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={!selectedAccountId || isConnecting}
            leftIcon={
              isConnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : undefined
            }
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}

export default AccountSelectorModal;
